// kontaktRollenService.ts — Rollenkatalog der Kontaktanlage (Status „Sonstige"),
// gepflegt in Einstellungen → Kontaktrollen. Lesen: alle; Schreiben: admin.
// Backend: apps/api/src/kontakt-rollen/kontaktRollen.controller.ts

import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import type { BezugsArt } from "../data/statusAuswahlKatalog";
import type { RollenFolge, RollenTyp } from "../data/kontaktRollen";

export type KontaktRolleEintrag = {
  id: number;
  /** Speicherwert — fest seit dem Anlegen. */
  value: string;
  label: string;
  typ: RollenTyp;
  bezug: BezugsArt | null;
  folge: RollenFolge | null;
  nurCoIntern: boolean;
  sortierung: number;
  aktiv: boolean;
};

export type KontaktRollenBereichEintrag = {
  id: number;
  titel: string;
  sortierung: number;
  aktiv: boolean;
  rollen: KontaktRolleEintrag[];
};

export type KontaktRollenKatalog = { bereiche: KontaktRollenBereichEintrag[] };

export type RollenEingabe = {
  label?: string;
  typ?: RollenTyp;
  bezug?: BezugsArt | null;
  folge?: RollenFolge | null;
  nurCoIntern?: boolean;
  aktiv?: boolean;
  bereichId?: number;
};

async function senden<T>(path: string, method: "POST" | "PUT" | "PATCH", body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `Fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

/** Der ganze Katalog des Mandanten, inklusive deaktivierter Einträge. */
export async function kontaktRollenLaden(signal?: AbortSignal): Promise<KontaktRollenKatalog> {
  const { data } = await apiFetch<KontaktRollenKatalog>("kontakt-rollen", { signal });
  return data;
}

export const bereichAnlegen = (titel: string) =>
  senden<KontaktRollenBereichEintrag>("kontakt-rollen/bereiche", "POST", { titel });

export const bereichAendern = (id: number, patch: { titel?: string; aktiv?: boolean }) =>
  senden<{ ok: true }>(`kontakt-rollen/bereiche/${id}`, "PATCH", patch);

export const bereicheSortieren = (ids: number[]) =>
  senden<{ ok: true }>("kontakt-rollen/bereiche/reihenfolge", "PUT", { ids });

export const rolleAnlegen = (bereichId: number, eingabe: RollenEingabe & { label: string }) =>
  senden<KontaktRolleEintrag>(`kontakt-rollen/bereiche/${bereichId}/rollen`, "POST", eingabe);

export const rolleAendern = (id: number, patch: RollenEingabe) =>
  senden<{ ok: true }>(`kontakt-rollen/rollen/${id}`, "PATCH", patch);

export const rollenSortieren = (bereichId: number, ids: number[]) =>
  senden<{ ok: true }>(`kontakt-rollen/bereiche/${bereichId}/rollen/reihenfolge`, "PUT", { ids });
